import { defineStore } from 'pinia'
import { z } from 'zod'
import { emailLogin, getEmailCode, verifyEmailCode, checkEmailRegistered, emailRegister, findPassword } from '~/apis/login'
import { useAuthStore } from '~/stores/authStore'
import { useToastHandler } from '~/composables/useToastHandler'

export const useLoginStore = defineStore('loginStore', () => {
  const authStore = useAuthStore()
  const { showErrorToast, showSuccessToast } = useToastHandler()

  type PageType = 'SignIn' | 'SignUpVerifyCode' | 'SignUpRegister' | 'ForgetPasswordSignUpVerifyCode' | 'ForgetPasswordSignUpRegister'

  type LogicError = Error & { __logicError?: boolean }
  const markLogicError = (message: string): LogicError => {
    const error = new Error(message) as LogicError
    error.__logicError = true
    return error
  }
  const isLogicError = (error: unknown): error is LogicError => Boolean((error as LogicError)?.__logicError)
  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message) return error.message
    if (typeof error === 'string' && error) return error
    if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
      return (error as any).message
    }
    return fallback
  }

  // 登录弹窗相关
  const isOpenLoginModal = ref(false)
  const fromMyProfile = ref(false)
  const pageType = ref<PageType>('SignIn')
  const openLoginModal = (pageTypeParam: PageType, options?: { fromMyProfile?: boolean }) => {
    fromMyProfile.value = Boolean(options?.fromMyProfile)
    togglePageType(pageTypeParam)
    isOpenLoginModal.value = true
  }
  const closeLoginModal = () => {
    fromMyProfile.value = false
    initializeModal()
    isOpenLoginModal.value = false
  }
  const togglePageType = (pageTypeParam: PageType, options?: { preserveSignUpContext?: boolean }) => {
    let preservedEmail = ''
    let preservedCode = ''
    if (options?.preserveSignUpContext) {
      preservedEmail = signUpEmail.value
      preservedCode = emailVerificationCode.value
    }
    initializeModal()
    if (options?.preserveSignUpContext) {
      signUpEmail.value = preservedEmail
      emailVerificationCode.value = preservedCode
    }
    pageType.value = pageTypeParam
  }

  // 初始化弹窗（关闭时使用和切换页面类型时使用）
  const initializeModal = () => {
    signInEmail.value = ''
    password.value = ''
    loginLoading.value = false
    signUpEmail.value = ''
    emailVerificationCode.value = ''
    codeSending.value = false
    codeVerifying.value = false
    countdown.value = 0
    hasSentCode.value = false
    signUpEmailError.value = ''
    emailVerificationCodeError.value = ''
    registerPassword.value = ''
    registerConfirmPassword.value = ''
    registerConfirmPasswordError.value = ''
    registerLoading.value = false
    showPasswordRequirements.value = false
    clearCountdownTimer()
  }

  // 登录相关
  const signInEmail = ref('')
  const password = ref('')
  const loginLoading = ref(false)
  const loginSchema = z.object({
    signInEmail: z
      .string()
      .nonempty('Please enter your Email address.')
      .email('Please enter a valid email address.'),
    password: z.string().nonempty('Please enter your Password.')
  })
  // 登录
  const handleLogin = async () => {
    await nextTick()
    if (loginLoading.value) return
    loginLoading.value = true

    try {
      const email = signInEmail.value.trim()
      signInEmail.value = email
      const response = await emailLogin({
        email,
        password: password.value
      })
      if (!response.success || !response.data?.token) {
        throw markLogicError(response.message || 'Incorrect email or password.')
      }
      await authStore.setSession({
        token: response.data.token,
        user: {
          ...(response.data.user || {}),
          downgrade: (response.data.user as any)?.downgrade ?? ''
        }
      })
      showSuccessToast('Login successful!')
      closeLoginModal()

      // 登录成功后，重定向到之前想访问的页面
      if (import.meta.client) {
        const redirectPath = sessionStorage.getItem('redirectAfterLogin')
        if (redirectPath && redirectPath !== '/') {
          sessionStorage.removeItem('redirectAfterLogin')
          const router = useRouter()
          router.push(redirectPath)
        }
      }
    } catch (error) {
      const fallback = 'Incorrect email or password.'
      const httpMessage
        = (error as any)?.response?.data?.message
          ?? (error as any)?.data?.message
          ?? (error as any)?.message
      // 一些 http 包装会返回形如 "[ERROR] [HTTP] ..." 的提示，改用兜底文案
      const normalizedMessage = httpMessage ? String(httpMessage) : ''
      const isHttpNoise = normalizedMessage.startsWith('[ERROR] [HTTP]') || normalizedMessage.startsWith('installHook.js:')
      const message = isHttpNoise
        ? fallback
        : (normalizedMessage || getErrorMessage(error, fallback))
      showErrorToast(message)
    } finally {
      loginLoading.value = false
    }
  }

  // 忘记密码 以及 注册-验证邮箱验证码相关
  const signUpEmail = ref('')
  const emailVerificationCode = ref('')
  const codeSending = ref(false)
  const codeVerifying = ref(false)
  const countdown = ref(0)
  const hasSentCode = ref(false) // 标记是否已发送过验证码
  const signUpEmailError = ref('')
  const emailVerificationCodeError = ref('') // 验证码错误提示
  const verifyEmailFormat = z
    .string()
    .trim()
    .nonempty('Please enter your Email address.')
    .email('Please enter a valid email address.')
  let countdownTimer: ReturnType<typeof setInterval> | null = null
  // 发送验证码
  const handleSendCode = async () => {
    const email = signUpEmail.value.trim()
    const validationResult = verifyEmailFormat.safeParse(email)
    if (!validationResult.success) {
      signUpEmailError.value = validationResult.error.issues[0]?.message ?? 'Please enter a valid email address.'
      return
    }
    signUpEmail.value = email
    signUpEmailError.value = ''

    if (countdown.value > 0 || codeSending.value) return

    codeSending.value = true
    try {
      const isForgetFlow = pageType.value === 'ForgetPasswordSignUpVerifyCode'
      const checkResponse = await checkEmailRegistered({ email })
      if (!checkResponse.success) {
        throw markLogicError(checkResponse.message || 'Unable to verify email status.')
      }
      const registered = checkResponse.data ?? false
      if (!isForgetFlow && registered) {
        signUpEmailError.value = 'This email is already registered. Please sign in.'
        return
      }
      if (isForgetFlow && !registered) {
        signUpEmailError.value = 'Account not found. Please sign up first.'
        return
      }

      const response = await getEmailCode({ email })
      if (!response.success) {
        throw markLogicError(response.message || 'Failed to send verification code.')
      }

      hasSentCode.value = true
      emailVerificationCodeError.value = '' // 发送验证码时清除之前的错误
      showSuccessToast('Verification code sent. Please check your inbox.')
      // Start a new countdown
      startCountdownTimer(60) // Default 60 seconds countdown
    } catch (error) {
      if (isLogicError(error)) {
        const errorMessage = getErrorMessage(error, 'Failed to send verification code.')
        // Check if the error message contains a cooldown time
        const match = errorMessage.match(/Cannot resend within (\d+) seconds?\.?/i)
        if (match && match[1]) {
          const remainingSeconds = parseInt(match[1], 10)
          if (!isNaN(remainingSeconds)) {
            // Start countdown with the remaining time from the error message
            startCountdownTimer(remainingSeconds)
            // Show a more user-friendly error message
            showErrorToast(`Please wait ${remainingSeconds} seconds before requesting a new code.`)
          } else {
            showErrorToast(errorMessage)
          }
        } else {
          showErrorToast(errorMessage)
        }
      }
    } finally {
      codeSending.value = false
    }
  }
  const codeVerifySchema = z.object({
    signUpEmail: z
      .string()
      .nonempty('Please enter your Email address.')
      .email('Please enter your Email address.'),
    emailVerificationCode: z.string().nonempty('Please enter your verification code.').regex(/^\d{6}$/, 'Incorrect verification code.')
  })
  // 验证验证码
  const handleCodeVerify = async () => {
    if (codeVerifying.value) return
    codeVerifying.value = true
    emailVerificationCodeError.value = '' // 清除之前的错误
    const email = signUpEmail.value.trim()
    const code = emailVerificationCode.value.trim()
    signUpEmail.value = email
    emailVerificationCode.value = code

    try {
      const response = await verifyEmailCode({ email, code })
      const verifySucceeded = response.code === 200
      if (!verifySucceeded) {
        const errorMessage = 'Incorrect email verification code.'
        emailVerificationCodeError.value = errorMessage
        showErrorToast(errorMessage)
        return
      }
      emailVerificationCodeError.value = '' // 验证成功，清除错误
      showSuccessToast('Verification successful. Please set your login password.')
      const nextPage = pageType.value === 'SignUpVerifyCode' ? 'SignUpRegister' : 'ForgetPasswordSignUpRegister'
      togglePageType(nextPage, { preserveSignUpContext: true })
    } catch (error) {
      // 对请求异常使用兜底提示，避免显示底层 URL/状态码
      const fallbackMessage = 'Incorrect email verification code.'
      const errorMessage = isLogicError(error) ? getErrorMessage(error, fallbackMessage) : fallbackMessage
      emailVerificationCodeError.value = errorMessage
      showErrorToast(errorMessage)
    } finally {
      codeVerifying.value = false
    }
  }

  // 注册-确认密码相关
  const registerPassword = ref('')
  const registerConfirmPassword = ref('')
  const registerConfirmPasswordError = ref('')
  const registerLoading = ref(false)
  const registerSchema = z
    .object({
      registerPassword: z.string(),
      registerConfirmPassword: z.string()
    })
    .superRefine((data, ctx) => {
      // 仅在确认密码非空时校验必填与一致性，避免对密码字段提示错误
      if (!data.registerConfirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['registerConfirmPassword'],
          message: 'Please confirm your Password.'
        })
        return
      }
      // 检查两个密码是否完全相等
      if (data.registerPassword !== data.registerConfirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['registerConfirmPassword'],
          message: 'Passwords do not match.'
        })
      }
    })
  const checkStrength = (str: string) => {
    const requirements = [
      { regex: /.{8,}/, text: 'At least 8 characters long' },
      { regex: /\d/, text: 'At least one number' },
      { regex: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?/~"'\\]/, text: 'At least one special character(e.g., !@#$%)' }
    ]

    return requirements.map(req => ({ met: req.regex.test(str), text: req.text }))
  }
  const strength = computed(() => checkStrength(registerPassword.value))
  // Password requirements 显隐
  const showPasswordRequirements = ref(false)
  const allStrengthMet = computed(() => strength.value.every(item => item.met))
  const onRegisterPasswordFocus = () => {
    showPasswordRequirements.value = true
  }
  const onRegisterPasswordBlur = () => {
    if (allStrengthMet.value) {
      showPasswordRequirements.value = false
    }
  }

  // 确认密码实时校验：
  // - 当确认密码长度 >= 密码长度时，若不相等则提示错误
  // - 或者失去焦点时不相等也提示错误
  // - 一旦有错误，直到两次密码相等前都会保持错误提示
  watch([registerConfirmPassword, registerPassword], ([confirm, password]) => {
    // 未输入确认密码时不提示错误
    if (!confirm) {
      registerConfirmPasswordError.value = ''
      return
    }

    const hasError = !!registerConfirmPasswordError.value
    const reachedLength = confirm.length >= password.length
    const shouldValidate = reachedLength || hasError

    if (shouldValidate && confirm !== password) {
      registerConfirmPasswordError.value = 'Passwords do not match.'
    } else if (confirm === password) {
      registerConfirmPasswordError.value = ''
    }
  })

  const onRegisterConfirmPasswordBlur = () => {
    const confirm = registerConfirmPassword.value
    const passwordValue = registerPassword.value

    if (!confirm) {
      registerConfirmPasswordError.value = ''
      return
    }

    if (confirm !== passwordValue) {
      registerConfirmPasswordError.value = 'Passwords do not match.'
    } else {
      registerConfirmPasswordError.value = ''
    }
  }
  // 注册
  const handleRegister = async () => {
    if (registerLoading.value) return

    // 先根据密码强度规则进行校验，未满足任一规则则禁止提交
    if (!allStrengthMet.value) {
      const firstUnmet = strength.value.find(item => !item.met)
      const message = firstUnmet?.text || 'Password does not meet the requirements.'
      showErrorToast(message)
      return
    }

    // 提交前再次校验两次密码是否符合规则且相等
    const result = registerSchema.safeParse({
      registerPassword: registerPassword.value,
      registerConfirmPassword: registerConfirmPassword.value
    })
    if (!result.success) {
      const confirmError = result.error.issues.find(issue => issue.path[0] === 'registerConfirmPassword')
      if (confirmError) {
        registerConfirmPasswordError.value = confirmError.message
        showErrorToast(confirmError.message)
      }
      return
    }

    registerLoading.value = true
    const email = signUpEmail.value.trim()
    const code = emailVerificationCode.value.trim()
    signUpEmail.value = email
    emailVerificationCode.value = code

    try {
      const isForgetFlow = pageType.value === 'ForgetPasswordSignUpRegister'
      if (isForgetFlow) {
        const response = await findPassword({
          email,
          password: registerPassword.value,
          code
        })
        if (!response.success || response.data?.success === false) {
          throw markLogicError(response.message || 'Failed to reset password.')
        }
        showSuccessToast('Password reset successfully. Please sign in.')
        const newPassword = registerPassword.value
        togglePageType('SignIn')
        signInEmail.value = email
        password.value = newPassword
        return
      }

      const eventID = email + '-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 8)
      const response = await emailRegister({
        email,
        password: registerPassword.value,
        code,
        event_id: eventID
      })
      if (!response.success || !response.data?.token) {
        throw markLogicError(response.message || 'Registration failed.')
      }
      await authStore.setSession({
        token: response.data.token,
        user: {
          ...(response.data.user || {}),
          downgrade: (response.data.user as any)?.downgrade ?? ''
        }
      })
      showSuccessToast('Registration successful! Start your creation now!')
      if (import.meta.client) {
        const gtag = (window as any)?.gtag
        if (typeof gtag === 'function') {
          gtag('event', 'conversion', { send_to: 'AW-17924652563/L7aECMqsl_EbEJP8keNC' })
        }
      }
      if (import.meta.client) {
        const fbq = (window as any)?.fbq
        if (typeof fbq === 'function') {
          fbq('track', 'CompleteRegistration', {}, {
            eventID: eventID
          })
        }
      }
      closeLoginModal()

      // 注册成功后，重定向到之前想访问的页面
      if (import.meta.client) {
        const redirectPath = sessionStorage.getItem('redirectAfterLogin')
        if (redirectPath && redirectPath !== '/') {
          sessionStorage.removeItem('redirectAfterLogin')
          const router = useRouter()
          router.push(redirectPath)
        }
      }
    } catch (error) {
      if (isLogicError(error)) {
        const fallback = pageType.value === 'ForgetPasswordSignUpRegister' ? 'Failed to reset password.' : 'Registration failed.'
        showErrorToast(getErrorMessage(error, fallback))
      }
    } finally {
      registerLoading.value = false
    }
  }

  const clearCountdownTimer = () => {
    if (countdownTimer) {
      clearInterval(countdownTimer)
      countdownTimer = null
    }
    countdown.value = 0
  }

  const startCountdownTimer = (duration: number) => {
    clearCountdownTimer()
    countdown.value = duration
    countdownTimer = setInterval(() => {
      countdown.value--
      if (countdown.value <= 0) {
        clearCountdownTimer()
      }
    }, 1000)
  }

  return {
    // 登录相关
    isOpenLoginModal,
    pageType,
    fromMyProfile,
    openLoginModal,
    closeLoginModal,
    togglePageType,
    signInEmail,
    password,
    loginLoading,
    loginSchema,
    handleLogin,
    signUpEmail,
    emailVerificationCode,
    codeSending,
    codeVerifying,
    countdown,
    hasSentCode,
    signUpEmailError,
    emailVerificationCodeError,
    handleSendCode,
    codeVerifySchema,
    handleCodeVerify,
    registerPassword,
    registerConfirmPassword,
    registerConfirmPasswordError,
    registerLoading,
    registerSchema,
    strength,
    showPasswordRequirements,
    onRegisterPasswordFocus,
    onRegisterPasswordBlur,
    onRegisterConfirmPasswordBlur,
    handleRegister
  }
})
